// src/components/course/interactive/InteractiveAssessment.jsx
import React, { useState } from 'react';
import {
  Target, CheckCircle, XCircle, RotateCcw, ArrowRight, GripVertical, Shuffle
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const InteractiveAssessment = ({ block, onComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);

  const questions = block.questions || [];
  const currentQuestion = questions[currentQuestionIndex];

  const handleSubmit = () => {
    // Calculate score
    let correctCount = 0;
    
    questions.forEach((question, index) => {
      const userAnswer = answers[index];
      let isCorrect = false;

      switch (question.type) {
        case 'multiple-choice':
          isCorrect = userAnswer === question.correctAnswer;
          break;
        case 'drag-drop':
          isCorrect = JSON.stringify(userAnswer) === JSON.stringify(question.correctOrder);
          break;
        case 'matching':
          isCorrect = JSON.stringify(userAnswer) === JSON.stringify(question.correctMatches);
          break;
      }

      if (isCorrect) correctCount++;
    });

    const total = questions.length;
    const percentage = Math.round((correctCount / total) * 100);
    
    setResults({ correct: correctCount, total, percentage });
    setSubmitted(true);
    onComplete({ correct: correctCount, total, percentage });
  };

  const handleReset = () => {
    setAnswers({});
    setSubmitted(false);
    setResults(null);
    setCurrentQuestionIndex(0);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  if (!questions || questions.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No assessment questions available</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <AssessmentResults 
        results={results} 
        questions={questions}
        answers={answers}
        onReset={handleReset}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">
          Question {currentQuestionIndex + 1} of {questions.length}
        </div>
        <div className="flex items-center space-x-2">
          {questions.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full ${
                index === currentQuestionIndex
                  ? 'bg-blue-600 w-4'
                  : answers[index] !== undefined
                  ? 'bg-green-500'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        {currentQuestion.type === 'multiple-choice' && (
          <MultipleChoiceQuestion
            question={currentQuestion}
            answer={answers[currentQuestionIndex]}
            onAnswer={(answer) => setAnswers({ ...answers, [currentQuestionIndex]: answer })}
          />
        )}

        {currentQuestion.type === 'drag-drop' && (
          <DragDropQuestion
            question={currentQuestion}
            answer={answers[currentQuestionIndex]}
            onAnswer={(answer) => setAnswers({ ...answers, [currentQuestionIndex]: answer })}
          />
        )}

        {currentQuestion.type === 'matching' && (
          <MatchingQuestion
            question={currentQuestion}
            answer={answers[currentQuestionIndex]}
            onAnswer={(answer) => setAnswers({ ...answers, [currentQuestionIndex]: answer })}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        {currentQuestionIndex === questions.length - 1 ? (
          <button
            onClick={handleSubmit}
            disabled={Object.keys(answers).length !== questions.length}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <CheckCircle className="w-5 h-5" />
            <span>Submit Assessment</span>
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <span>Next</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Multiple Choice Question Component
const MultipleChoiceQuestion = ({ question, answer, onAnswer }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">{question.question}</h3>
      
      {question.instruction && (
        <p className="text-sm text-gray-600">{question.instruction}</p>
      )}

      <div className="space-y-2">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => onAnswer(index)}
            className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
              answer === index
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                answer === index
                  ? 'border-blue-500 bg-blue-500'
                  : 'border-gray-300'
              }`}>
                {answer === index && (
                  <div className="w-2 h-2 bg-white rounded-full" />
                )}
              </div>
              <span className="flex-1">{option}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Drag and Drop Question Component
const DragDropQuestion = ({ question, answer, onAnswer }) => {
  const [items, setItems] = useState(answer || [...question.items]);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      onAnswer(newItems.map(item => item.id));
    }
  };

  const handleShuffle = () => {
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    setItems(shuffled);
    onAnswer(shuffled.map(item => item.id));
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">{question.question}</h3>
      
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {question.instruction || 'Drag items to arrange them in the correct order'}
        </p>
        <button
          onClick={handleShuffle}
          className="flex items-center space-x-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Shuffle className="w-4 h-4" />
          <span>Shuffle</span>
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((item, index) => (
              <SortableItem key={item.id} id={item.id} item={item} index={index} />
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <div className="bg-white border-2 border-blue-500 rounded-lg p-4 shadow-lg">
              {items.find(item => item.id === activeId)?.text}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

// Sortable Item Component
const SortableItem = ({ id, item, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-gray-300 cursor-move"
    >
      <div className="flex items-center space-x-3">
        <div {...attributes} {...listeners} className="text-gray-400 hover:text-gray-600">
          <GripVertical className="w-5 h-5" />
        </div>
        <span className="flex-1">{item.text}</span>
        <span className="text-sm text-gray-500">#{index + 1}</span>
      </div>
    </div>
  );
};

// Matching Question Component
const MatchingQuestion = ({ question, answer, onAnswer }) => {
  const [matches, setMatches] = useState(answer || {});
  const [selectedLeft, setSelectedLeft] = useState(null);

  const handleLeftClick = (leftIndex) => {
    setSelectedLeft(leftIndex);
  };

  const handleRightClick = (rightIndex) => {
    if (selectedLeft !== null) {
      const newMatches = { ...matches, [selectedLeft]: rightIndex };
      setMatches(newMatches);
      onAnswer(newMatches);
      setSelectedLeft(null);
    }
  };

  const clearMatch = (leftIndex) => {
    const newMatches = { ...matches };
    delete newMatches[leftIndex];
    setMatches(newMatches);
    onAnswer(newMatches);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">{question.question}</h3>
      
      <p className="text-sm text-gray-600">
        {question.instruction || 'Click an item on the left, then click its match on the right'}
      </p>

      <div className="grid grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-2">
          {question.leftItems.map((item, index) => (
            <button
              key={index}
              onClick={() => handleLeftClick(index)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                selectedLeft === index
                  ? 'border-blue-500 bg-blue-50'
                  : matches[index] !== undefined
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{item}</span>
                {matches[index] !== undefined && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearMatch(index);
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Right Column */}
        <div className="space-y-2">
          {question.rightItems.map((item, index) => {
            const isMatched = Object.values(matches).includes(index);
            const canSelect = selectedLeft !== null;
            
            return (
              <button
                key={index}
                onClick={() => canSelect && handleRightClick(index)}
                disabled={!canSelect}
                className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                  isMatched
                    ? 'border-green-500 bg-green-50'
                    : canSelect
                    ? 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                    : 'border-gray-200 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{item}</span>
                  {isMatched && <CheckCircle className="w-4 h-4 text-green-600" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Progress */}
      <div className="text-sm text-gray-600 text-center">
        {Object.keys(matches).length} of {question.leftItems.length} matched
      </div>
    </div>
  );
};

// Assessment Results Component
const AssessmentResults = ({ results, questions, answers, onReset }) => {
  return (
    <div className="space-y-6">
      {/* Overall Score */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border-2 border-blue-200 p-8 text-center">
        <div className="text-6xl font-bold text-blue-600 mb-2">
          {results.percentage}%
        </div>
        <p className="text-lg text-gray-700 mb-4">
          You scored {results.correct} out of {results.total}
        </p>
        
        {results.percentage >= 80 ? (
          <div className="flex items-center justify-center space-x-2 text-green-600">
            <CheckCircle className="w-6 h-6" />
            <span className="font-semibold">Great job! You passed!</span>
          </div>
        ) : results.percentage >= 60 ? (
          <div className="flex items-center justify-center space-x-2 text-yellow-600">
            <Target className="w-6 h-6" />
            <span className="font-semibold">Good effort! Keep practicing.</span>
          </div>
        ) : (
          <div className="flex items-center justify-center space-x-2 text-red-600">
            <XCircle className="w-6 h-6" />
            <span className="font-semibold">Review the material and try again.</span>
          </div>
        )}
      </div>

      {/* Question Review */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Review Answers</h3>
        
        {questions.map((question, index) => {
          const userAnswer = answers[index];
          let isCorrect = false;

          switch (question.type) {
            case 'multiple-choice':
              isCorrect = userAnswer === question.correctAnswer;
              break;
            case 'drag-drop':
              isCorrect = JSON.stringify(userAnswer) === JSON.stringify(question.correctOrder);
              break;
            case 'matching':
              isCorrect = JSON.stringify(userAnswer) === JSON.stringify(question.correctMatches);
              break;
          }

          return (
            <div
              key={index}
              className={`p-4 rounded-lg border-2 ${
                isCorrect
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-start space-x-3">
                {isCorrect ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-gray-900 mb-2">
                    Question {index + 1}: {question.question}
                  </p>
                  {question.explanation && (
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Explanation:</strong> {question.explanation}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Retry Button */}
      <button
        onClick={onReset}
        className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
      >
        <RotateCcw className="w-5 h-5" />
        <span>Retry Assessment</span>
      </button>
    </div>
  );
};

export default InteractiveAssessment;